import { escapeHtml, formatDateFR } from '../../utils/dateFormat';
import { serviceLabel } from '../../utils/kitchenTasks';

export const STATES = [
  { value: 'ouverte', label: 'Demandée', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'terminee', label: 'Terminée', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
  { value: 'non_realisee', label: 'Non réalisée', cls: 'bg-red-50 text-red-700 border-red-300' },
  { value: 'annulee', label: 'Annulée', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
];
export const stateMeta = (v) => STATES.find(s => s.value === v) || STATES[0];
export const SOURCES = [{ value: 'tache', label: 'Tâche' }, { value: 'swipe', label: 'Swipe' }];
export const sourceLabel = (v) => SOURCES.find(s => s.value === v)?.label || v;

const RAW_STATUS = { a_faire: 'À faire', en_cours: 'En cours', refusee: 'À corriger', terminee: 'Terminée', validee: 'Validée', a_valider: 'À valider', non_realisee: 'Non réalisée', annulee: 'Annulée', pending: 'À faire', passee: 'Passée', done: 'Fait' };
export const rawStatusLabel = (v) => RAW_STATUS[v] || v || '';

export const qty = (q, u) => (q == null || q === '' ? '' : `${q} ${u || ''}`.trim());
export const who = (r) => (r.assigned_user_names || []).join(', ');

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

export const quickRange = (key, today) => {
  const t = fromIso(today);
  if (key === 'today') return { from: today, to: today };
  if (key === '7d') { const d = new Date(t); d.setDate(d.getDate() - 6); return { from: iso(d), to: today }; }
  if (key === 'week') { const d = new Date(t); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); const e = new Date(d); e.setDate(d.getDate() + 6); return { from: iso(d), to: iso(e) }; }
  if (key === 'month') return { from: `${today.slice(0, 7)}-01`, to: iso(new Date(t.getFullYear(), t.getMonth() + 1, 0)) };
  return null;
};

export const groupByDay = (rows) => {
  const days = new Map();
  rows.forEach(r => {
    if (!days.has(r.task_date)) days.set(r.task_date, new Map());
    const svc = days.get(r.task_date);
    if (!svc.has(r.service)) svc.set(r.service, []);
    svc.get(r.service).push(r);
  });
  return [...days.entries()].map(([date, svc]) => ({ key: date, label: formatDateFR(date), groups: [...svc.entries()].map(([s, items]) => ({ key: s, label: serviceLabel(s), rows: items })) }));
};

export const groupByCollaborator = (rows) => {
  const map = new Map();
  const add = (name, r) => { if (!map.has(name)) map.set(name, []); map.get(name).push(r); };
  rows.forEach(r => {
    const names = new Set(r.assigned_user_names || []);
    if (r.executed_by_name) names.add(r.executed_by_name);
    if (names.size === 0) add('Non affectée', r); else names.forEach(n => add(n, r));
  });
  return [...map.entries()].sort((a, b) => (a[0] === 'Non affectée') - (b[0] === 'Non affectée') || a[0].localeCompare(b[0]))
    .map(([name, items]) => ({ key: name, label: name, groups: [{ key: 'all', label: `${items.length} ligne${items.length > 1 ? 's' : ''}`, rows: items }] }));
};

const frDate = (s) => (s ? s.split('-').reverse().join('/') : '');
const frDateTime = (iso) => { if (!iso) return ''; const d = new Date(iso); return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('fr-FR', { timeZone: 'Europe/Zurich', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', ''); };

// Colonnes validées par le chef (export CSV, impression et tableau) — rien de plus pour le moment
export const COLUMNS = [
  ['Date', r => frDate(r.task_date)], ['Tâche', r => r.title], ['Qté demandée', r => r.planned_quantity ?? ''], ['Unité', r => r.unit || ''],
  ['Qté produite', r => r.actual_quantity ?? ''], ['Unité produite', r => r.actual_unit || ''], ['État', r => stateMeta(r.state).label],
  ['Affectée à', who], ['Exécutée par', r => r.executed_by_name || ''], ['Terminée le', r => frDateTime(r.completed_at)],
  ['Motif non réalisation', r => r.not_done_reason || ''], ['Commentaire', r => r.collaborator_comment || ''],
];

export const buildCsv = (rows) => {
  const cell = (v) => { const s = String(v ?? '').replace(/"/g, '""'); return /[";\n]/.test(s) ? `"${s}"` : s; };
  const lines = [COLUMNS.map(([h]) => cell(h)).join(';'), ...rows.map(r => COLUMNS.map(([, f]) => cell(f(r))).join(';'))];
  return `\ufeff${lines.join('\r\n')}`;
};

export const downloadCsv = (rows, period) => {
  const blob = new Blob([buildCsv(rows)], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `rapport-mise-en-place_${period.from}_${period.to}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
};

const e = escapeHtml;
const HEAD = `<thead><tr>${COLUMNS.map(([h]) => `<th>${e(h)}</th>`).join('')}</tr></thead>`;
const row = (r) => `<tr>${COLUMNS.map(([, f]) => `<td>${e(f(r))}</td>`).join('')}</tr>`;
export const buildPrintHtml = (data, sections) => {
  const s = data.summary;
  const units = data.quantities_by_unit.map(u => `<li>${e(u.unit)} : demandé ${e(u.planned)} · produit ${e(u.actual)}</li>`).join('');
  const collabs = data.by_collaborator.map(c => `<tr><td>${e(c.name)}</td><td>${c.affectees}</td><td>${c.terminees}</td><td>${c.non_realisees}</td><td>${c.ouvertes}</td><td>${c.taux_realisation == null ? '—' : `${c.taux_realisation} %`}</td></tr>`).join('');
  const body = sections.map(sec => `<h2>${e(sec.label)}</h2>${sec.groups.map(g => `<h3>${e(g.label)} (${g.rows.length})</h3><table>${HEAD}<tbody>${g.rows.map(row).join('')}</tbody></table>`).join('')}`).join('');
  return `<h1>Rapport de mise en place</h1><p>Période du ${e(formatDateFR(data.period.from))} au ${e(formatDateFR(data.period.to))} · imprimé le ${e(new Date().toLocaleString('fr-FR', { timeZone: 'Europe/Zurich' }))}</p>
<div class="totals"><span><b>${s.demandees}</b> demandées</span><span><b>${s.terminees}</b> terminées</span><span><b>${s.non_realisees}</b> non réalisées</span><span><b>${s.ouvertes}</b> encore ouvertes</span><span><b>${s.annulees}</b> annulées</span><span>Taux : <b>${s.taux_realisation == null ? '—' : `${s.taux_realisation} %`}</b></span></div>
${units ? `<p><b>Quantités (par unité identique)</b></p><ul>${units}</ul>` : ''}
<h2>Récapitulatif par collaborateur</h2><table><thead><tr><th>Collaborateur</th><th>Affectées</th><th>Terminées</th><th>Non réalisées</th><th>Ouvertes</th><th>Taux</th></tr></thead><tbody>${collabs}</tbody></table>${body}`;
};

export const printReport = (data, sections) => {
  let iframe = document.getElementById('report-print-iframe');
  if (!iframe) {
    iframe = document.createElement('iframe');
    iframe.id = 'report-print-iframe';
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;';
    document.body.appendChild(iframe);
  }
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write('<!DOCTYPE html><html lang="fr"><head><title>Rapport de mise en place</title><style>*{font-family:"Helvetica Neue",Arial,sans-serif;margin:0}body{padding:20px;font-size:11px;color:#111}h1{font-size:20px;margin-bottom:4px}h2{font-size:15px;margin:18px 0 6px;border-bottom:2px solid #111;padding-bottom:3px}h3{font-size:12px;margin:10px 0 4px;color:#444}p{margin:4px 0}ul{margin:4px 0 8px 18px}table{width:100%;border-collapse:collapse;margin-bottom:6px}th,td{padding:3px 5px;text-align:left;vertical-align:top;border-bottom:1px solid #ddd}th{border-bottom:1px solid #999;font-size:10px;text-transform:uppercase}.totals{display:flex;gap:16px;flex-wrap:wrap;margin:10px 0;padding:8px;border:1px solid #ccc;border-radius:6px}small{color:#666}@media print{body{padding:8px}}</style></head><body>');
  doc.write(buildPrintHtml(data, sections));
  doc.write('</body></html>');
  doc.close();
  setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); }, 300);
};
